// Pet.jsx — the centerpiece of Kids Mode. A big animated sprite that
// reacts to mood (derived from hunger). Image-heavy, zero text.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**) is
// the explicit opt-in to emojis per the plan. No-emoji rule still
// holds elsewhere in the app.

import { useRef, useState } from "react";
import { COLOR } from "../../lib/tokens";
import { petFallbackFor, MOOD_FACE } from "./petSpriteMap";
import { ALIEN_SLUG, alienImageFor } from "./alienStateMap";
import { useInlinePetEmbed } from "../../lib/useInlinePetEmbed";

// IS_IOS_NATIVE was used to gate the inline 3D embed; the embed
// was retired 2026-05-27 in favor of static PNG + explicit
// VIEW IN 3D / SEE IN AR buttons. Keep the import line above
// in case future iOS-only logic needs it.

// Bundle-relative path to the idle USDZ. Lives in /public/ so both
// the web build (skipped — IS_IOS_NATIVE guard) and the native iOS
// bundle (BoodaInlinePetView resolves via Bundle.main/public/…)
// agree on the location.
const IDLE_USDZ = "/img/Alien/alien_idle.usdz";

// Mood -> idle animation timing. Sleepy pets breathe slowly; happy
// pets bounce faster.
const IDLE_TIMING = {
  happy:   { duration: 0.9, scale: 1.06 },
  content: { duration: 1.4, scale: 1.04 },
  calm:    { duration: 1.8, scale: 1.03 },
  sleepy:  { duration: 2.4, scale: 1.015 },
};

export function Pet({ pet, mood, onTap }) {
  const [tapped, setTapped] = useState(false);
  const slug = pet?.pet_slug || "booda";
  const inlineRef = useRef(null);
  // Inline 3D embed retired 2026-05-27. The 3D-overlay-on-top-of-the-
  // WebView was visually jittery during scroll + couldn't be easily
  // centered inside the aura ring. PetArButton now exposes VIEW IN 3D
  // (fullscreen rotatable scene) + SEE IN AR (camera + real-world
  // placement) as explicit user actions; the Pet tab itself always
  // shows the static PNG sprite.
  const useInline3D = false;
  const fallback = petFallbackFor(slug);
  // Resolution order for the displayed body sprite:
  //   1. pet_catalog.art_bundle (migration 0222) — the per-pet 16-
  //      state map keyed on "boost|spark|build|super" bit strings.
  //      Catches Peach, Bob, Evans, Steve, Princess, Lavender,
  //      and any future pet with state art.
  //   2. Legacy alienStateMap.js fallback — the original alien
  //      stayed wired through this hardcoded map; once its
  //      art_bundle gets populated this branch becomes dead.
  //   3. Static image_url for pets without state-driven art.
  //   4. Emoji fallback from petSpriteMap (the * sprite tile in
  //      the dev/early-art phase before bundles exist).
  const stateKey = `${pet?.has_boost ? 1 : 0}${pet?.has_spark ? 1 : 0}${pet?.has_build ? 1 : 0}${pet?.has_super ? 1 : 0}`;
  const bundleHit = pet?.art_bundle?.[stateKey] || pet?.art_bundle?.["0000"] || pet?.art_bundle?.default;
  const imageUrl = bundleHit
    || (slug === ALIEN_SLUG
        ? alienImageFor({
            has_boost: pet?.has_boost === true,
            has_spark: pet?.has_spark === true,
            has_build: pet?.has_build === true,
            has_super: pet?.has_super === true,
          })
        : pet?.image_url);
  const timing = IDLE_TIMING[mood] || IDLE_TIMING.content;

  const handleTap = () => {
    setTapped(true);
    setTimeout(() => setTapped(false), 600);
    if (onTap) onTap();
  };

  // Native overlay swallows touches by default, so the inline-pet
  // hook bridges its tap gesture back through "petTapped" — wire it
  // to the same handleTap the button uses so the mood-pulse + sfx
  // path fires identically whether the kid taps the 3D model or the
  // PNG fallback.
  useInlinePetEmbed({
    ref: inlineRef,
    filename: useInline3D ? IDLE_USDZ : null,
    onTap: handleTap,
  });

  return (
    <div
      style={{
        position: "relative",
        width: "min(70vw, 320px)",
        height: "min(70vw, 320px)",
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Soft aura — subtler when sleepy, brighter when happy.
          Renders as the mood signal a kid actually reads. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${fallback.accent}33, transparent 65%)`,
          opacity: mood === "sleepy" ? 0.3 : mood === "calm" ? 0.5 : mood === "content" ? 0.7 : 0.9,
          transition: "opacity 600ms ease",
        }}
      />
      <button
        type="button"
        onClick={handleTap}
        aria-label={pet?.pet_name || "pet"}
        style={{
          all: "unset",
          cursor: "pointer",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          touchAction: "manipulation",
        }}
      >
        {useInline3D ? (
          // Placeholder div the native overlay sits on top of. Sized
          // by the parent button. The native side renders its own
          // gradient backdrop + 3D scene; this div is empty + sized
          // only so the hook has a rect to track. Sub-1px border so
          // an unmounted-overlay scenario doesn't leave a blank spot.
          <div
            ref={inlineRef}
            aria-hidden
            style={{
              width:  "100%",
              height: "100%",
              borderRadius: "24px",
              background: "transparent",
            }}
          />
        ) : (
          /* Pet sprite: real SVG if loaded, big emoji fallback. The
             idle animation is a CSS keyframe per mood. */
          <span
            style={{
              fontSize: "min(50vw, 220px)",
              lineHeight: 1,
              display: "inline-block",
              width: "100%",
              height: "100%",
              transform: tapped ? "scale(1.12) rotate(-4deg)" : `scale(${timing.scale})`,
              animation: tapped
                ? "none"
                : `kid-pet-idle-${mood} ${timing.duration}s ease-in-out infinite alternate`,
              transition: "transform 200ms ease",
              filter: mood === "sleepy" ? "saturate(0.7) brightness(0.96)" : "none",
              display: imageUrl ? "flex" : "inline-block",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {imageUrl
              ? <img src={imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
              : fallback.emoji}
          </span>
        )}
      </button>

      {/* Mood face tucked into a corner — a secondary, smaller signal
          for kids who notice the detail. Never shows a number. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          bottom: "6%",
          right: "6%",
          fontSize: 56,
          lineHeight: 1,
          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.08))",
          transition: "transform 300ms ease",
          transform: tapped ? "scale(1.2)" : "scale(1)",
        }}
      >
        {MOOD_FACE[mood] || MOOD_FACE.content}
      </div>

      {/* Keyframes injected once per mount. Cheap and self-contained.
          Different mood = different breathing rhythm, all subtle. */}
      <style>{`
        @keyframes kid-pet-idle-happy {
          0%   { transform: scale(1.00) translateY(0); }
          100% { transform: scale(1.06) translateY(-6px); }
        }
        @keyframes kid-pet-idle-content {
          0%   { transform: scale(1.00); }
          100% { transform: scale(1.04); }
        }
        @keyframes kid-pet-idle-calm {
          0%   { transform: scale(1.00); }
          100% { transform: scale(1.03); }
        }
        @keyframes kid-pet-idle-sleepy {
          0%   { transform: scale(1.00) rotate(-1deg); }
          100% { transform: scale(1.015) rotate(1deg); }
        }
      `}</style>
    </div>
  );
}

export default Pet;
